import React, { useState, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import { IconButton, Tooltip } from "@material-ui/core";
import { Mic, Stop, MicOff } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { fileUploadClient } from "services/axiosClient";
import AiLoadingIndicator from "components/AiLoadingIndicator";

// Colors
const primaryColor = "#00acc1";
const dangerColor = "#f44336";
const grayColor = "#999999";

const useStyles = makeStyles({
  container: {
    display: "inline-flex",
    alignItems: "center",
    marginLeft: "8px",
  },
  recordButton: {
    padding: "6px",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "scale(1.1)",
    },
  },
  recording: {
    color: dangerColor,
    animation: "$pulse 1.5s infinite",
  },
  idle: {
    color: grayColor,
    "&:hover": {
      color: primaryColor,
    },
  },
  processing: {
    color: primaryColor,
  },
  "@keyframes pulse": {
    "0%": {
      opacity: 1,
    },
    "50%": {
      opacity: 0.5,
    },
    "100%": {
      opacity: 1,
    },
  },
  timer: {
    fontSize: "11px",
    color: dangerColor,
    marginLeft: "4px",
    fontFamily: "monospace",
  },
});

/**
 * AudioRecorder Component
 *
 * A compact audio recording button that captures audio and sends it
 * to the backend for speech-to-text transcription.
 *
 * Props:
 * - onTranscription: Callback function that receives the transcribed text
 * - apiEndpoint: The endpoint for speech-to-text API (relative path)
 * - disabled: Whether the button is disabled
 */
const AudioRecorder = ({ onTranscription, apiEndpoint, disabled }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [error, setError] = useState(null);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  // Process and send audio to backend
  const processAudio = useCallback(async () => {
    if (audioChunksRef.current.length === 0) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Create audio blob
      const audioBlob = new Blob(audioChunksRef.current, {
        type: "audio/webm",
      });

      // Create form data
      const formData = new FormData();
      formData.append("audio", audioBlob, "recording.webm");

      // Use fileUploadClient which handles auth automatically
      const response = await fileUploadClient.post(apiEndpoint, formData);

      if (response.data.success && response.data.text) {
        onTranscription(response.data.text);
        setError(null);
      } else {
        setError(
          response.data.error ||
            t("ctsguide.documentation.transcription_error"),
        );
      }
    } catch (err) {
      console.error("Error processing audio:", err);
      setError(t("ctsguide.documentation.transcription_error"));
    } finally {
      setIsProcessing(false);
      audioChunksRef.current = [];
    }
  }, [apiEndpoint, onTranscription, t]);

  // Start recording
  const startRecording = useCallback(async () => {
    try {
      setError(null);
      audioChunksRef.current = [];

      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
        },
      });
      streamRef.current = stream;

      // Determine best supported format
      let mimeType = "audio/webm";
      if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) {
        mimeType = "audio/webm;codecs=opus";
      } else if (MediaRecorder.isTypeSupported("audio/mp4")) {
        mimeType = "audio/mp4";
      } else if (MediaRecorder.isTypeSupported("audio/ogg")) {
        mimeType = "audio/ogg";
      }

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        // Process the recorded audio
        processAudio();
      };

      mediaRecorder.start(1000); // Collect data every second
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (err) {
      console.error("Error starting recording:", err);
      if (err.name === "NotAllowedError") {
        setError(t("ctsguide.documentation.mic_permission_denied"));
      } else {
        setError(t("ctsguide.documentation.mic_error"));
      }
    }
  }, [t, processAudio]);

  // Stop recording
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();

      // Stop all tracks
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }

      // Clear timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      setIsRecording(false);
    }
  }, [isRecording]);

  // Handle click
  const handleClick = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  // Determine icon and tooltip
  let icon;
  let tooltipText;
  let buttonClass;

  if (isRecording) {
    icon = <Stop fontSize="small" />;
    tooltipText = t("ctsguide.documentation.stop_recording");
    buttonClass = classes.recording;
  } else if (error) {
    icon = <MicOff fontSize="small" />;
    tooltipText = error;
    buttonClass = classes.idle;
  } else {
    icon = <Mic fontSize="small" />;
    tooltipText = t("ctsguide.documentation.start_recording");
    buttonClass = classes.idle;
  }

  // When processing, show the AI loading indicator instead of button
  if (isProcessing) {
    return (
      <div className={classes.container}>
        <AiLoadingIndicator
          isLoading={true}
          context="speech"
          interval={1500}
          color={primaryColor}
        />
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <Tooltip title={tooltipText} arrow>
        <span>
          <IconButton
            className={`${classes.recordButton} ${buttonClass}`}
            onClick={handleClick}
            disabled={disabled}
            size="small"
          >
            {icon}
          </IconButton>
        </span>
      </Tooltip>
      {isRecording && (
        <span className={classes.timer}>{formatTime(recordingTime)}</span>
      )}
    </div>
  );
};

AudioRecorder.propTypes = {
  onTranscription: PropTypes.func.isRequired,
  apiEndpoint: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
};

AudioRecorder.defaultProps = {
  disabled: false,
};

export default AudioRecorder;
